// =============================================================================
// BookingPricingEditor.tsx — the hourly booking price form (FORT-288).
//
// One row per facility type the club has: an hourly rate, how that type charges a
// part hour (pro-rata or a fixed part-hour price), and the part-hour price. A type
// left without an hourly rate keeps the standard slot pricing. The form only edits settings — prices are
// calculated by the API (BookingPriceCalculator), never here.
//
// Shared by the club admins' Booking Pricing page (BookingPricingManagement) and the
// Fortius admin settings shell (BookingPricingSection); each supplies its own load and
// save calls.
// =============================================================================

import React, { useMemo, useState } from 'react';
import { Card } from '../../../ui/primitives/Card';
import { Button } from '../../../ui/primitives/Button';
import { Text } from '../../../ui/primitives/Text';
import { Stack } from '../../../ui/primitives/Stack';
import { Input } from '../../../ui/primitives/Input';
import { Alert } from '../../../ui/primitives/Alert';
import { Select } from '../../../ui/primitives/Select';
import { Table, TableColumn } from '../../../ui/primitives/Table';
import {
  BookingPricingSettings,
  FacilityTypeRate,
  PartHourCharging,
} from '../../../model/client/ClientSettings';
import { UpdateClientBookingPricingRequest } from '../../../model/client/UpdateClientBookingPricingRequest';

// Mirrors BookingPricingSettings.MaxPrice on the API.
const MAX_PRICE = 10000;
const PRICE_PATTERN = /^\d+(\.\d{1,2})?$/;

export interface RateRow {
  facilityType: string;
  hourlyRate: string;
  partHourCharging: PartHourCharging;
  partHourPrice: string;
}

const PART_HOUR_OPTIONS: { value: PartHourCharging; label: string }[] = [
  { value: 'proRata', label: 'Pro-rata' },
  { value: 'partHourPrice', label: 'Part-hour price' },
];

// 'TreatmentRoom' -> 'Treatment Room'
export const facilityTypeLabel = (facilityType: string): string =>
  facilityType.replace(/([a-z])([A-Z])/g, '$1 $2');

const toInput = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);

// A blank field is "not set"; anything else must be a price 0..MAX_PRICE with at most 2dp.
export const parsePrice = (raw: string): { value: number | null; valid: boolean } => {
  const trimmed = raw.trim();
  if (trimmed === '') return { value: null, valid: true };
  if (!PRICE_PATTERN.test(trimmed)) return { value: null, valid: false };
  const value = Number(trimmed);
  return { value, valid: value <= MAX_PRICE };
};

export function buildRows(facilityTypes: string[], pricing: BookingPricingSettings): RateRow[] {
  // Every facility type the club has, plus any type already priced (so a stored
  // rate is never silently dropped on save).
  const names = new Map<string, string>();
  [...facilityTypes, ...pricing.facilityTypeRates.map((r) => r.facilityType)].forEach((name) => {
    if (!names.has(name.toLowerCase())) names.set(name.toLowerCase(), name);
  });
  return Array.from(names.values()).map((facilityType) => {
    const rate = pricing.facilityTypeRates.find(
      (r) => r.facilityType.toLowerCase() === facilityType.toLowerCase()
    );
    // A rate without its own mode comes from an API that predates per-type modes (the
    // frontend deploys ahead of the API), which sends the club-wide mode instead; reading
    // it keeps a save in that window from switching the club's types to pro-rata.
    const mode = rate?.partHourCharging ?? pricing.partHourCharging;
    // A 'partHourPrice' type without a price is charged pro-rata by the API, so show
    // it as what it is rather than as an error the admin never made.
    const partHourCharging: PartHourCharging =
      mode === 'partHourPrice' && rate?.partHourPrice != null ? 'partHourPrice' : 'proRata';
    return {
      facilityType,
      hourlyRate: toInput(rate?.hourlyRate),
      partHourCharging,
      partHourPrice: toInput(rate?.partHourPrice),
    };
  });
}

// Why a row's part-hour price can't be saved, or undefined when it can.
export function partHourPriceError(row: RateRow): string | undefined {
  // A pro-rata type's part-hour price is neither used nor saved.
  if (row.partHourCharging !== 'partHourPrice') return undefined;
  const partHour = parsePrice(row.partHourPrice);
  const hourly = parsePrice(row.hourlyRate);
  if (!partHour.valid) return 'Enter a price, e.g. 6 or 6.50';
  // A part-hour price with no hourly rate would be silently ignored.
  if (partHour.value !== null && hourly.value === null) return 'Set an hourly rate first';
  // The API would quietly fall back to pro-rata, which isn't what was chosen.
  if (partHour.value === null && hourly.value !== null) {
    return 'Enter the part-hour price';
  }
  return undefined;
}

// Rows with an hourly rate become rates; a row without one stays on slot pricing.
// Returns null when any field is invalid.
export function toRates(rows: RateRow[]): FacilityTypeRate[] | null {
  const rates: FacilityTypeRate[] = [];
  for (const row of rows) {
    const hourly = parsePrice(row.hourlyRate);
    if (!hourly.valid || partHourPriceError(row)) return null;
    if (hourly.value === null) continue;
    rates.push({
      facilityType: row.facilityType,
      hourlyRate: hourly.value,
      // A pro-rata type never uses its part-hour price, so it isn't saved.
      partHourPrice: row.partHourCharging === 'partHourPrice' ? parsePrice(row.partHourPrice).value : null,
      partHourCharging: row.partHourCharging,
    });
  }
  return rates;
}

// The save payload. Each rate carries its own mode; the top-level partHourCharging is
// only for an API that predates per-type modes (the frontend deploys ahead of the API),
// which applies it to every rate. 'partHourPrice' there, with no part-hour price on the
// pro-rata rates, charges exactly what the per-type modes say: that API already charges
// a type without a part-hour price pro-rata.
export function toSaveRequest(rates: FacilityTypeRate[]): UpdateClientBookingPricingRequest {
  return {
    facilityTypeRates: rates,
    partHourCharging: rates.some((r) => r.partHourCharging === 'partHourPrice') ? 'partHourPrice' : 'proRata',
  };
}

export interface BookingPricingEditorProps {
  facilityTypes: string[];
  pricing: BookingPricingSettings;
  currencySymbol: string;
  // Throws on failure; the message is shown and the edits are kept.
  onSave: (request: UpdateClientBookingPricingRequest) => Promise<void>;
}

export const BookingPricingEditor: React.FC<BookingPricingEditorProps> = ({
  facilityTypes,
  pricing,
  currencySymbol,
  onSave,
}) => {
  const initialRows = useMemo(() => buildRows(facilityTypes, pricing), [facilityTypes, pricing]);
  const [rows, setRows] = useState<RateRow[]>(initialRows);
  const [savedRows, setSavedRows] = useState<RateRow[]>(initialRows);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const rates = useMemo(() => toRates(rows), [rows]);
  const dirty = JSON.stringify(savedRows) !== JSON.stringify(rows);

  const updateRow = <K extends 'hourlyRate' | 'partHourPrice' | 'partHourCharging'>(
    facilityType: string,
    field: K,
    value: RateRow[K]
  ) =>
    setRows((current) => current.map((row) => (row.facilityType === facilityType ? { ...row, [field]: value } : row)));

  const handleSave = async () => {
    if (!rates) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(toSaveRequest(rates));
      setSavedRows(rows);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save booking prices.');
    } finally {
      setSaving(false);
    }
  };

  const columns: TableColumn<RateRow>[] = [
    {
      title: 'Facility type',
      dataIndex: 'facilityType',
      key: 'facilityType',
      render: (facilityType: string) => <Text weight="medium">{facilityTypeLabel(facilityType)}</Text>,
    },
    {
      title: 'Hourly rate',
      key: 'hourlyRate',
      render: (_: unknown, row: RateRow) => (
        <Input
          data-testid={`booking-pricing-hourly-${row.facilityType}`}
          inputMode="decimal"
          prefix={currencySymbol}
          placeholder="Not set"
          value={row.hourlyRate}
          error={parsePrice(row.hourlyRate).valid ? undefined : 'Enter a price, e.g. 8 or 8.50'}
          onChange={(e) => updateRow(row.facilityType, 'hourlyRate', e.target.value)}
        />
      ),
    },
    {
      title: 'Part hours',
      key: 'partHourCharging',
      render: (_: unknown, row: RateRow) => (
        <Select
          data-testid={`booking-pricing-part-hour-charging-${row.facilityType}`}
          aria-label={`Part hours for ${facilityTypeLabel(row.facilityType)}`}
          value={row.partHourCharging}
          options={PART_HOUR_OPTIONS}
          // An unpriced type stays on slot pricing, so its part-hour mode would not be saved.
          disabled={parsePrice(row.hourlyRate).value === null}
          onChange={(value) => updateRow(row.facilityType, 'partHourCharging', value as PartHourCharging)}
        />
      ),
    },
    {
      title: 'Part-hour price',
      key: 'partHourPrice',
      render: (_: unknown, row: RateRow) => {
        const usesPrice = row.partHourCharging === 'partHourPrice';
        return (
          <Input
            data-testid={`booking-pricing-part-hour-${row.facilityType}`}
            inputMode="decimal"
            prefix={currencySymbol}
            placeholder={usesPrice ? 'Required' : 'Not used'}
            disabled={!usesPrice}
            value={row.partHourPrice}
            error={partHourPriceError(row)}
            onChange={(e) => updateRow(row.facilityType, 'partHourPrice', e.target.value)}
          />
        );
      },
    },
  ];

  return (
    <Stack gap={5} data-testid="booking-pricing-editor">
      <Card header="Hourly rates">
        <Stack gap={3}>
          <Text variant="body" color="secondary">
            Bookings are charged the hourly rate for each whole hour. For each facility type, choose how the
            rest of a booking that isn't a whole hour (for example the last 30 minutes of a 1h30 booking) is
            charged: a share of the hourly rate (pro-rata), or a fixed part-hour price. Leave the hourly rate
            blank to keep a facility type on its standard slot pricing.
          </Text>
          <Table<RateRow>
            data-testid="booking-pricing-table"
            columns={columns}
            dataSource={rows}
            rowKey="facilityType"
            pagination={false}
            emptyText="This club has no facility types yet."
          />
        </Stack>
      </Card>

      {saveError && <Alert type="error" message={saveError} data-testid="booking-pricing-save-error" />}

      <Stack direction="horizontal" gap={3} align="center">
        <Button
          variant="primary"
          data-testid="booking-pricing-save"
          disabled={!dirty || rates === null}
          loading={saving}
          onClick={handleSave}
        >
          Save prices
        </Button>
        <Text variant="caption">New bookings are priced with the saved rates straight away.</Text>
      </Stack>
    </Stack>
  );
};
